import React from 'react'
import PropTypes from 'prop-types'
import { CheckboxIcon, RadioIcon, LoadingIcon } from '../../../core/icons'
import { Level, LevelItem, LevelLeft } from '../../../bulma'
import { helpers } from '../../../../helpers'

/**
* Single filter item that represents a filter option to used in a sidebar filter
* component.
*/
function FilterItem(props) {
  const {
    children, count, allowMultiple, fallbackUrl, isSelected, isLoading, isDisabled, onClick, className, ...rest
  } = props

  /**
  * onClick event handler. If disabled, or already selected and the parent filter
  * doesn't allow more than one option then the action is ignored to not make
  * a redundant request.
  */
  const onClickHandler = (e) => {
    e.preventDefault()

    if (isDisabled || (isSelected && !allowMultiple)) {
      return
    }
    onClick()
  }

  /**
  * onKeyDown event handler. Calls the `onClickHandler` if the user presses the
  * `enter` key.
  */
  const onKeyDownHandler = (e) => {
    if (e.keyCode !== 13) {
      return
    }
    onClickHandler(e)
  }

  // Parse text for count/label.
  const parsedCount = parseInt(count)
  const countText = parsedCount ? helpers.formatNumber(parsedCount) : count

  // Determine which icon should be displayed.
  let icon

  if (allowMultiple) {
    icon = <CheckboxIcon isOn={isSelected} />
  } else {
    icon = <RadioIcon isOn={isSelected} />
  }

  const classes = [
    'filter-item',
    isSelected ? 'is-selected' : '',
    isDisabled ? 'is-disabled' : '',
    className || '',
  ]

  return (
    <li {...rest} className={classes.join(' ')}>
      <a
        tabIndex={isDisabled ? null : '0'}
        onClick={onClickHandler}
        onKeyDown={onKeyDownHandler}
        href={fallbackUrl}
      >
        <Level isMobile>
          <LevelLeft>
            <LevelItem>{icon}</LevelItem>
            <LevelItem className="can-shrink has-ellipsis">{children}</LevelItem>
            {
              countText ? (
                <LevelItem>
                  <span className="has-text-muted is-size-xs">
                    {countText}
                    {parsedCount ? <span className="sr-only">Products</span> : false}
                  </span>
                </LevelItem>
              ) : false
            }
            {isLoading ? <LevelItem><LoadingIcon /></LevelItem> : false}
          </LevelLeft>
        </Level>
      </a>
    </li>
  )
}

FilterItem.defaultProps = {
  allowMultiple: false,
  count: '',
  isDisabled: false,
  isLoading: false,
  isSelected: false,
}

FilterItem.propTypes = {
  // If parent filter allows multiple filters to be selected.
  allowMultiple: PropTypes.bool,
  // Label text (usually for the aggregation count).
  count: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  // Fallback url for when the filter is clicked. Mostly for SEO.
  fallbackUrl: PropTypes.string,
  // If filter item can be clicked or not.
  isDisabled: PropTypes.bool,
  // If more products are being loaded, so filter item might change.
  isLoading: PropTypes.bool,
  // If filter item is currently applied.
  isSelected: PropTypes.bool,
}

export default FilterItem
